// ============================================================================
// Hooks TanStack Query — ACCUEIL E1a : lectures du tableau de bord social
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (visibilite etage 3 des publications, 403 autoritaire sur indicateurs/
// comites/equipes — D4) ; le SPA gate les 3 requetes management AVANT fetch
// sur viewer.canViewManagement du fil (FIX-403 D2 — zero requete refusee
// emise, anti « delegated 403 stream » Sev1 juin) et ne MONTE les cartes que
// sur ce meme booleen (D3, controles capability-aware §4c). Un 503
// MIGRATION_144_PENDING (tables gest.* absentes) = bandeau explicite, pas un
// etat d'erreur retryable.

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, ApiServerError } from '@gs/api-client';

// ─── Types miroir des DTOs serveur (server/repositories/dashboard.ts) ────────

export type PublicationCategory = 'general' | 'contrats' | 'rh' | 'evenements' | 'securite' | 'operations';
export const PUBLICATION_CATEGORIES: PublicationCategory[] = [
  'general', 'contrats', 'rh', 'evenements', 'securite', 'operations',
];

export interface Publication {
  id: string;
  title: string;
  body: string;
  category: PublicationCategory;
  authorEmail: string;
  authorName: string | null;
  postedAt: string;
  reactions: { like: number; love: number; celebrate: number };
  viewCount: number;
  commentCount: number;
}

export interface PublicationsPage {
  publications: Publication[];
  nextCursor: string | null;
  viewer: {
    role: 'admin' | 'manager' | 'director' | 'employee';
    canPublish: boolean;
    /** Gating capability-aware AVANT fetch (FIX-403 D2) : les 3 requêtes management
     *  (indicateurs/comités/santé) ne partent QUE si true — zéro requête refusée émise. */
    canViewManagement: boolean;
  };
}

export interface IndicatorComparison {
  value: number;
  vsBudget: number;
  vsPriorYear: number;
}

export interface IndicatorsSummary {
  month: string | null;
  revenue: IndicatorComparison | null;
  netProfit: IndicatorComparison | null;
  pulses: { answered: number; total: number; mood: number | null; period: string };
  postings: { count: number };
}

export interface CommitteeActivity {
  id: string;
  name: string;
  milestonesDone: number;
  milestonesTotal: number;
  lastActivityAt: string;
}

export interface CommitteesActivity {
  committees: CommitteeActivity[];
  total: number;
}

export interface TeamHealth {
  id: string;
  name: string;
  directorName: string | null;
  moodScore: number | null;
}

export interface TeamsHealth {
  period: string;
  answered: number;
  total: number;
  mood: number | null;
  teams: TeamHealth[];
}

// ─── Etats capability (§4c) — le 403 n'est PLUS un etat client : les requetes
// management sont gatees AVANT fetch sur viewer.canViewManagement (FIX-403 D2/D3,
// anti « delegated 403 stream ») ; seul le 503 migration reste a detecter. ──────

export function isMigrationPending(err: unknown): boolean {
  return err instanceof ApiServerError && err.status === 503;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

const KEY = ['gest', 'accueil'] as const;

/** Fil pagine keyset — pages concatenees par useInfiniteQuery (E1a lecture). */
export function usePublicationsFeed(category: PublicationCategory | null, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...KEY, 'publications', { category }],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (pageParam) params.set('cursor', pageParam);
      const qs = params.toString();
      return api.get<PublicationsPage>(`/api/publications${qs ? `?${qs}` : ''}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

export function useIndicatorsSummary(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'indicators'],
    queryFn: () => api.get<IndicatorsSummary>('/api/indicators/summary'),
    enabled,
    // 503 (migration pendante) est un etat STABLE — zero retry. (Le 403 n'est
    // plus atteignable en usage normal : enabled = canViewManagement, D2.)
    retry: false,
  });
}

export function useCommitteesActivity(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'committees'],
    queryFn: () => api.get<CommitteesActivity>('/api/committees/activity'),
    enabled,
    retry: false,
  });
}

export function useTeamsHealth(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, 'teams'],
    queryFn: () => api.get<TeamsHealth>('/api/teams/health'),
    enabled,
    retry: false,
  });
}
